import React, { useState } from 'react';
import {
  Container, Typography, Box, Paper, Tabs, Tab, Stack, Chip,
} from '@mui/material';
import {
  SmartToy as SmartToyIcon,
  Memory as MemoryIcon,
  PlayCircleOutline as PlayIcon,
  Timeline as TimelineIcon,
  AutoFixHigh as RLIcon,
  Security as AuditIcon,
  RecordVoiceOver as ChatIcon,
  Hub as HubIcon,
  Storage as StorageIcon,
  AccountTree as TreeIcon,
  Sync as SyncIcon,
  GitHub as GitHubIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Code as CodeIcon,
  Psychology as PsychologyIcon,
  CheckCircle as CheckIcon,
  Assignment as TaskIcon,
  Feedback as FeedbackIcon,
  Lock as LockIcon,
  WifiTethering as WebRTCIcon,
  RecordVoiceOver as STTIcon,
  VolumeUp as TTSIcon,
  Cloud as CloudIcon,
  Warning as GuardIcon,
  Loop as LoopIcon,
  Bolt as TriggerIcon,
  SupportAgent as AgentIcon,
  Block as BlockIcon,
  NotificationsActive as NotifyIcon,
  AdminPanelSettings as AdminIcon,
  AutoAwesome as DynamicIcon,
  Extension as ExtensionIcon,
  LibraryAdd as LibraryIcon,
  Analytics as AnalyticsIcon,
  Recommend as RecommendIcon,
  ManageSearch as ScanIcon,
  CompareArrows as MatchIcon,
  AddCircle as AddIcon,
  EditNote as EditRoleIcon,
  Build as BuildIcon,
  PlayDisabled as HaltIcon,
  ErrorOutline as RiskIcon,
} from '@mui/icons-material';

// ─────────────────────────────────────────────
// Primitive Building Blocks
// ─────────────────────────────────────────────

function Node({ icon, title, subtitle, color = '#1976d2', variant = 'default', sx = {} }) {
  const bg = variant === 'highlight'
    ? color
    : variant === 'ghost'
    ? 'transparent'
    : `${color}12`;
  const textColor = variant === 'highlight' ? '#fff' : color;
  const borderStyle = variant === 'ghost' ? 'dashed' : 'solid';

  return (
    <Box sx={{
      border: `2px ${borderStyle} ${color}`,
      borderRadius: 2,
      px: 1.5,
      py: 1,
      backgroundColor: bg,
      display: 'inline-flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 0.4,
      minWidth: 110,
      ...sx,
    }}>
      {icon && (
        <Box sx={{ color: textColor, display: 'flex', fontSize: 22, lineHeight: 1 }}>
          {icon}
        </Box>
      )}
      <Typography variant="caption" fontWeight={700} sx={{ color: textColor, fontSize: '0.72rem', lineHeight: 1.2 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: variant === 'highlight' ? 'rgba(255,255,255,0.8)' : 'text.secondary', fontSize: '0.62rem', lineHeight: 1.2 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}

function ArrowDown({ label, color = '#9e9e9e' }) {
  return (
    <Stack alignItems="center" sx={{ py: 0.3 }}>
      {label && <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', fontStyle: 'italic', mb: 0.2 }}>{label}</Typography>}
      <Box sx={{ width: 2, height: 18, backgroundColor: color, mx: 'auto' }} />
      <Box sx={{
        width: 0, height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: `7px solid ${color}`,
      }} />
    </Stack>
  );
}

function ArrowRight({ label, color = '#9e9e9e' }) {
  return (
    <Stack direction="row" alignItems="center" sx={{ px: 0.5 }}>
      {label && <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', mr: 0.3 }}>{label}</Typography>}
      <Box sx={{ height: 2, width: 20, backgroundColor: color }} />
      <Box sx={{
        width: 0, height: 0,
        borderTop: '5px solid transparent',
        borderBottom: '5px solid transparent',
        borderLeft: `7px solid ${color}`,
      }} />
    </Stack>
  );
}


function Group({ title, color = '#1976d2', children, sx = {} }) {
  return (
    <Box sx={{
      border: `1.5px dashed ${color}50`,
      borderRadius: 2.5,
      p: 1.5,
      position: 'relative',
      ...sx,
    }}>
      {title && (
        <Typography variant="caption" sx={{
          position: 'absolute', top: -10, left: 12,
          backgroundColor: 'background.paper',
          px: 0.8,
          color,
          fontWeight: 700,
          fontSize: '0.65rem',
          textTransform: 'uppercase',
          letterSpacing: 0.8,
        }}>
          {title}
        </Typography>
      )}
      {children}
    </Box>
  );
}

function LegendItem({ color, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.8}>
      <Box sx={{ width: 14, height: 14, borderRadius: 0.5, backgroundColor: `${color}20`, border: `2px solid ${color}` }} />
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
    </Stack>
  );
}

function DiagramHeader({ title, description }) {
  return (
    <Box sx={{ mb: 3, textAlign: 'center' }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 680, mx: 'auto' }}>{description}</Typography>
    </Box>
  );
}

function DiagramCanvas({ children }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2, backgroundColor: '#fafafa', overflowX: 'auto' }}>
      {children}
    </Paper>
  );
}

// ─────────────────────────────────────────────
// Diagram 1: Product Overview
// ─────────────────────────────────────────────

function ProductOverviewDiagram() {
  const C = {
    user: '#1976d2',
    platform: '#7b1fa2',
    aiEmployee: '#0097a7',
    memory: '#00796b',
    agents: '#f57c00',
    infra: '#455a64',
    external: '#616161',
  };

  return (
    <Box>
      <DiagramHeader
        title="AI Employee — Product Architecture Overview"
        description="High-level product architecture showing how AI Employees integrate with the Tymeline platform, external tools, and management workflows."
      />
      <DiagramCanvas>
        {/* Layer 1: Users */}
        <Group title="User Layer" color={C.user}>
          <Stack direction="row" justifyContent="center" spacing={3} flexWrap="wrap" gap={2}>
            <Node icon={<PersonIcon />} title="Manager / Owner" subtitle="Hires, assigns, reviews" color={C.user} />
            <Node icon={<CodeIcon />} title="Developer" subtitle="Reviews PRs, gives feedback" color={C.user} />
            <Node icon={<BusinessIcon />} title="HR Manager" subtitle="Reviews HR tasks" color={C.user} />
          </Stack>
        </Group>

        <Stack alignItems="center"><ArrowDown color={C.platform} /></Stack>

        {/* Layer 2: Tymeline Platform */}
        <Group title="Tymeline Platform" color={C.platform} sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="center" alignItems="center">

            {/* Project Engine */}
            <Stack alignItems="center" spacing={1}>
              <Node icon={<TreeIcon />} title="Project Engine" subtitle="Epics / Stories / Tasks" color={C.platform} sx={{ minWidth: 130 }} />
            </Stack>

            <ArrowRight color={C.platform} />

            {/* AI Employee — Center Focus */}
            <Box sx={{ border: `3px solid ${C.aiEmployee}`, borderRadius: 2.5, p: 2, backgroundColor: `${C.aiEmployee}10`, minWidth: 200 }}>
              <Stack alignItems="center" spacing={1.5}>
                <Typography variant="caption" fontWeight={800} sx={{ color: C.aiEmployee, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
                  ★ AI Employee
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <Node icon={<MemoryIcon />} title="Memory" subtitle="Personal + Shared" color={C.memory} sx={{ minWidth: 90 }} />
                  <Node icon={<AgentIcon />} title="Agents" subtitle="Cursor, GitHub…" color={C.agents} sx={{ minWidth: 90 }} />
                </Stack>
                <Node icon={<PsychologyIcon />} title="LLM Execution Engine" subtitle="Planning + Decision" color={C.aiEmployee} sx={{ width: '100%' }} />
              </Stack>
            </Box>

            <ArrowRight color={C.platform} />

            {/* Tracking */}
            <Stack alignItems="center" spacing={1}>
              <Node icon={<TimelineIcon />} title="Activity Tracker" subtitle="All events logged" color={C.platform} sx={{ minWidth: 130 }} />
              <ArrowDown color={C.platform} />
              <Node icon={<LockIcon />} title="Tymeline ID" subtitle="Identity + Proof" color={C.infra} sx={{ minWidth: 130 }} />
            </Stack>

          </Stack>
        </Group>

        <Stack alignItems="center"><ArrowDown color={C.agents} /></Stack>

        {/* Layer 3: Agent Execution */}
        <Group title="Agent Execution Layer" color={C.agents} sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="center" spacing={2} flexWrap="wrap" gap={2}>
            <Node icon={<CloudIcon />} title="Cursor Agent" subtitle="Software tasks" color={C.agents} />
            <Node icon={<GitHubIcon />} title="GitHub Agent" subtitle="PR management" color={C.agents} />
            <Node icon={<HubIcon />} title="Web Search" subtitle="Research tasks" color={C.agents} />
            <Node icon={<AgentIcon />} title="SMTP Agent" subtitle="Email automation" color={C.agents} />
            <Node icon={<StorageIcon />} title="ATS / HRIS" subtitle="HR role tasks" color={C.agents} />
          </Stack>
        </Group>

        <Stack alignItems="center"><ArrowDown color={C.external} /></Stack>

        {/* Layer 4: External Systems */}
        <Group title="External Systems & Integrations" color={C.external}>
          <Stack direction="row" justifyContent="center" spacing={2} flexWrap="wrap" gap={2}>
            <Node icon={<GitHubIcon />} title="GitHub" subtitle="Code & PRs" color={C.external} />
            <Node icon={<CloudIcon />} title="Cursor Cloud" subtitle="AI coding" color={C.external} />
            <Node icon={<LockIcon />} title="Kwickly" subtitle="Identity provider" color={C.external} />
            <Node icon={<StorageIcon />} title="MongoDB" subtitle="Vector + data store" color={C.external} />
            <Node icon={<BusinessIcon />} title="Payment Service" subtitle="License management" color={C.external} />
          </Stack>
        </Group>

        {/* Legend */}
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }} justifyContent="center">
          <LegendItem color={C.user} label="User Layer" />
          <LegendItem color={C.platform} label="Tymeline Platform" />
          <LegendItem color={C.aiEmployee} label="AI Employee Core" />
          <LegendItem color={C.memory} label="Memory System" />
          <LegendItem color={C.agents} label="Agent Execution" />
          <LegendItem color={C.external} label="External Systems" />
        </Stack>
      </DiagramCanvas>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Diagram 2: Task Execution (Epic/Story)
// ─────────────────────────────────────────────

function TaskExecutionDiagram() {
  const C = {
    trigger: '#1565c0',
    plan: '#6a1b9a',
    exec: '#2e7d32',
    software: '#0277bd',
    hr: '#e65100',
    complete: '#558b2f',
    review: '#f57f17',
    rl: '#d32f2f',
    state: '#455a64',
  };

  return (
    <Box>
      <DiagramHeader
        title="Task Execution Pipeline — Epic / Story"
        description="End-to-end flow of how an AI Employee receives, plans, and executes a task, including asynchronous software execution and synchronous HR execution paths."
      />
      <DiagramCanvas>

        {/* States Banner */}
        <Group title="State Machine" color={C.state} sx={{ mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} flexWrap="wrap" gap={1}>
            {['pending', 'planning', 'running', 'completed / failed'].map((s, i, arr) => (
              <React.Fragment key={s}>
                <Chip label={s} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700, backgroundColor: `${C.state}15`, border: `1px solid ${C.state}` }} />
                {i < arr.length - 1 && <Typography sx={{ color: C.state, fontWeight: 700 }}>→</Typography>}
              </React.Fragment>
            ))}
          </Stack>
        </Group>

        {/* Trigger */}
        <Stack alignItems="center" spacing={0}>
          <Node icon={<TriggerIcon />} title="TRIGGER" subtitle="Auto (schedule) or Manual (dashboard)" color={C.trigger} variant="highlight" sx={{ minWidth: 260 }} />
          <ArrowDown color={C.plan} label="Task enters 'pending'" />

          {/* Planning Phase */}
          <Group title="Planning Phase" color={C.plan} sx={{ width: '100%', maxWidth: 520, mb: 0 }}>
            <Stack alignItems="center" spacing={1.5}>
              <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center" gap={1.5}>
                <Node icon={<CheckIcon />} title="Capability Validation" subtitle="Role & agent check" color={C.plan} sx={{ minWidth: 120 }} />
                <Node icon={<MemoryIcon />} title="Vector Memory Recall" subtitle="Employee + Project lanes" color={C.plan} sx={{ minWidth: 140 }} />
                <Node icon={<GuardIcon />} title="Reinforcement Rules" subtitle="Pinned constraints injected" color={C.rl} sx={{ minWidth: 140 }} />
              </Stack>
              <ArrowDown color={C.plan} />
              <Node icon={<PsychologyIcon />} title="LLM Builds Execution Plan" subtitle="Agent selection + tool assignment" color={C.plan} variant="highlight" sx={{ minWidth: 280 }} />
            </Stack>
          </Group>

          <ArrowDown color={C.exec} label="Task moves to 'running'" />

          {/* Fork */}
          <Node icon={<SyncIcon />} title="Agent Launched" subtitle="Based on role type" color={C.exec} sx={{ minWidth: 220 }} />
          <ArrowDown color={C.exec} />
        </Stack>

        {/* Two branches */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="center" alignItems="flex-start" sx={{ mt: 1 }}>

          {/* Software Role Path */}
          <Group title="Software Role Path" color={C.software} sx={{ flex: 1, minWidth: 220 }}>
            <Stack alignItems="center" spacing={0}>
              <Node icon={<CloudIcon />} title="Cursor Cloud Agent" subtitle="Launched asynchronously" color={C.software} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.software} label="Task stays 'running'" />
              <Node icon={<SyncIcon />} title="ReconcileCursorAgentTasks" subtitle="Cron polls for completion" color={C.software} variant="ghost" sx={{ minWidth: 180 }} />
              <ArrowDown color={C.software} />
              <Node icon={<GitHubIcon />} title="GitHub PR Created" subtitle="Code changes submitted" color={C.software} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.review} />
              <Node icon={<PersonIcon />} title="Human Review & Approval" subtitle="PR reviewed, comments added" color={C.review} sx={{ minWidth: 180 }} />
            </Stack>
          </Group>

          {/* HR Role Path */}
          <Group title="HR Assistant Path" color={C.hr} sx={{ flex: 1, minWidth: 220 }}>
            <Stack alignItems="center" spacing={0}>
              <Node icon={<AgentIcon />} title="ATS / HRIS Integration" subtitle="Synchronous execution" color={C.hr} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.hr} label="Intent detected:" />
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} justifyContent="center">
                <Chip label="QA" size="small" sx={{ backgroundColor: `${C.hr}15`, border: `1px solid ${C.hr}`, fontSize: '0.65rem' }} />
                <Chip label="Semantic Search" size="small" sx={{ backgroundColor: `${C.hr}15`, border: `1px solid ${C.hr}`, fontSize: '0.65rem' }} />
                <Chip label="Job Posting" size="small" sx={{ backgroundColor: `${C.hr}15`, border: `1px solid ${C.hr}`, fontSize: '0.65rem' }} />
              </Stack>
              <ArrowDown color={C.hr} />
              <Node icon={<CheckIcon />} title="Direct LLM Answer" subtitle="Returned synchronously" color={C.hr} sx={{ minWidth: 180 }} />
            </Stack>
          </Group>
        </Stack>

        {/* Merge back */}
        <Stack alignItems="center" spacing={0} sx={{ mt: 2 }}>
          <ArrowDown color={C.complete} label="Both paths converge" />
          <Node icon={<CheckIcon />} title="TASK COMPLETED" subtitle="Status → completed" color={C.complete} variant="highlight" sx={{ minWidth: 260 }} />
          <ArrowDown color={C.complete} />
          <Node icon={<LockIcon />} title="Tymeline ID Record Created" subtitle="Proof of Execution for this Epic" color={C.complete} sx={{ minWidth: 260 }} />
          <ArrowDown color={C.rl} label="Optional feedback" />
          <Node icon={<FeedbackIcon />} title="Reinforcement Feedback" subtitle="@via PR comments → RL Loop" color={C.rl} sx={{ minWidth: 260 }} />
        </Stack>

        {/* Legend */}
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }} justifyContent="center">
          <LegendItem color={C.trigger} label="Trigger" />
          <LegendItem color={C.plan} label="Planning" />
          <LegendItem color={C.software} label="Software Path" />
          <LegendItem color={C.hr} label="HR Assistant Path" />
          <LegendItem color={C.complete} label="Completion" />
          <LegendItem color={C.rl} label="Reinforcement" />
        </Stack>
      </DiagramCanvas>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Diagram 3: Activity Management
// ─────────────────────────────────────────────

function ActivityManagementDiagram() {
  const C = {
    source: '#1565c0',
    logger: '#6a1b9a',
    storage: '#2e7d32',
    record: '#f57c00',
    access: '#00838f',
  };

  const eventTypes = [
    { label: 'Task Planning', color: C.source },
    { label: 'Agent Execution', color: C.source },
    { label: 'Memory Recall', color: C.source },
    { label: 'Memory Update', color: C.source },
    { label: 'PR Created', color: C.source },
    { label: 'PR Approved', color: C.source },
    { label: 'Feedback Received', color: C.source },
    { label: 'Status Change', color: C.source },
    { label: 'Reinforcement Write', color: C.source },
  ];

  return (
    <Box>
      <DiagramHeader
        title="Activity Management — Tracking & Observability"
        description="Every action taken by an AI Employee is captured, stored, and made visible to authorized users through a comprehensive activity timeline."
      />
      <DiagramCanvas>

        {/* Event Sources */}
        <Group title="Activity Event Sources" color={C.source}>
          <Stack direction="row" flexWrap="wrap" gap={1.5} justifyContent="center">
            {eventTypes.map(e => (
              <Chip key={e.label} label={e.label} size="small" sx={{ backgroundColor: `${e.color}12`, border: `1px solid ${e.color}60`, fontWeight: 600, fontSize: '0.7rem' }} />
            ))}
          </Stack>
        </Group>

        <Stack alignItems="center"><ArrowDown color={C.logger} label="All events emit to logger" /></Stack>

        {/* Activity Logger */}
        <Stack alignItems="center" sx={{ mb: 0 }}>
          <Node icon={<TimelineIcon />} title="Activity Logger" subtitle="Real-time event capture" color={C.logger} variant="highlight" sx={{ minWidth: 260 }} />
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.storage} /></Stack>

        {/* Storage */}
        <Stack alignItems="center">
          <Node icon={<StorageIcon />} title="Secure MongoDB Storage" subtitle="Timestamped, immutable records" color={C.storage} sx={{ minWidth: 260 }} />
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.storage} /></Stack>

        {/* Parallel outputs */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="center" alignItems="stretch">

          <Group title="Proof of Execution" color={C.record} sx={{ flex: 1, textAlign: 'center' }}>
            <Stack alignItems="center" spacing={1.5}>
              <Node icon={<LockIcon />} title="Tymeline ID Record" subtitle="Created per Epic completion" color={C.record} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.record} />
              <Node icon={<CheckIcon />} title="Permanent Contribution Record" subtitle="Linked to AI Employee + Epic" color={C.record} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.record} />
              <Node icon={<BusinessIcon />} title="Portfolio Evidence" subtitle="Transferable across projects" color={C.record} sx={{ minWidth: 180 }} />
            </Stack>
          </Group>

          <Group title="Activity Timeline View" color={C.access} sx={{ flex: 1, textAlign: 'center' }}>
            <Stack alignItems="center" spacing={1.5}>
              <Node icon={<TimelineIcon />} title="Full Chronological Timeline" subtitle="All events in order" color={C.access} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.access} />
              <Node icon={<PersonIcon />} title="Authorized Access Only" subtitle="Manager / Owner / Auditor" color={C.access} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.access} />
              <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" gap={1}>
                <Node icon={<TreeIcon />} title="Decision Audit" subtitle="Why AI chose this action" color={C.access} sx={{ minWidth: 120 }} />
                <Node icon={<TaskIcon />} title="Task History" subtitle="Cross-project contributions" color={C.access} sx={{ minWidth: 120 }} />
              </Stack>
            </Stack>
          </Group>

        </Stack>

        {/* Legend */}
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }} justifyContent="center">
          <LegendItem color={C.source} label="Event Sources" />
          <LegendItem color={C.logger} label="Activity Logger" />
          <LegendItem color={C.storage} label="Secure Storage" />
          <LegendItem color={C.record} label="Proof of Execution" />
          <LegendItem color={C.access} label="Management Access" />
        </Stack>
      </DiagramCanvas>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Diagram 4: Reinforcement Learning
// ─────────────────────────────────────────────

function ReinforcementDiagram() {
  const C = {
    feedback: '#1565c0',
    bridge: '#6a1b9a',
    extract: '#e65100',
    store: '#2e7d32',
    inject: '#d32f2f',
    guard: '#f57c00',
    exec: '#00838f',
  };

  return (
    <Box>
      <DiagramHeader
        title="Reinforcement Memory Loop — Complete Learning Cycle"
        description="How human feedback continuously improves AI Employee behavior through a feedback ingestion, extraction, and injection pipeline."
      />
      <DiagramCanvas>

        {/* Feedback Sources */}
        <Group title="Human Feedback Sources" color={C.feedback}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" flexWrap="wrap" gap={2}>
            <Node icon={<GitHubIcon />} title="GitHub PR Comment" subtitle="@via tag with instructions" color={C.feedback} sx={{ minWidth: 150 }} />
            <Node icon={<ChatIcon />} title="Via Chat Command" subtitle="Direct feedback in chat" color={C.feedback} sx={{ minWidth: 150 }} />
            <Node icon={<PersonIcon />} title="Human Review" subtitle="PR approval / rejection" color={C.feedback} sx={{ minWidth: 150 }} />
          </Stack>
        </Group>

        <Stack alignItems="center"><ArrowDown color={C.bridge} label="captured by" /></Stack>

        {/* Bridge Capture */}
        <Stack alignItems="center">
          <Node icon={<HubIcon />} title="Via Bridge" subtitle="Unified feedback ingestion service" color={C.bridge} variant="highlight" sx={{ minWidth: 280 }} />
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.extract} label="passes to" /></Stack>

        {/* LLM Extraction */}
        <Stack alignItems="center">
          <Group title="LLM Extraction Service" color={C.extract} sx={{ maxWidth: 480, width: '100%' }}>
            <Stack alignItems="center" spacing={1.5}>
              <Node icon={<PsychologyIcon />} title="LLM Processes Feedback" subtitle="Structures raw feedback into rules" color={C.extract} sx={{ minWidth: 250 }} />
              <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1} justifyContent="center">
                <Chip label="📌 Rule" size="small" sx={{ backgroundColor: `${C.extract}15`, border: `1px solid ${C.extract}`, fontWeight: 700 }} />
                <Chip label="🏷 Category" size="small" sx={{ backgroundColor: `${C.extract}15`, border: `1px solid ${C.extract}`, fontWeight: 700 }} />
                <Chip label="🎯 Scope" size="small" sx={{ backgroundColor: `${C.extract}15`, border: `1px solid ${C.extract}`, fontWeight: 700 }} />
                <Chip label="📊 Confidence Level" size="small" sx={{ backgroundColor: `${C.extract}15`, border: `1px solid ${C.extract}`, fontWeight: 700 }} />
              </Stack>
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.store} label="indexed as vector" /></Stack>

        {/* MongoDB Vector Store */}
        <Stack alignItems="center">
          <Node icon={<StorageIcon />} title="MongoDB Vector Index" subtitle="memory_kind = reinforcement" color={C.store} sx={{ minWidth: 280 }} />
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.inject} label="on next task execution" /></Stack>

        {/* Context Injection + Guardrails */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="center" alignItems="stretch">

          <Group title="Context Injection" color={C.inject} sx={{ flex: 1 }}>
            <Stack alignItems="center" spacing={1.5}>
              <Node icon={<LoopIcon />} title="Recall Phase" subtitle="Rules retrieved via vector search" color={C.inject} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.inject} />
              <Node icon={<GuardIcon />} title="Pinned as MUST-FOLLOW" subtitle="Injected above normal context" color={C.inject} variant="highlight" sx={{ minWidth: 180 }} />
              <ArrowDown color={C.inject} />
              <Node icon={<PsychologyIcon />} title="LLM Constrained Execution" subtitle="Rules act as absolute constraints" color={C.inject} sx={{ minWidth: 180 }} />
            </Stack>
          </Group>

          <Group title="Safety Guardrails" color={C.guard} sx={{ flex: 1 }}>
            <Stack spacing={1.5}>
              <Node icon={<LockIcon />} title="Kill-Switch Rules" subtitle="Halt dangerous actions" color={C.guard} sx={{ width: '100%' }} />
              <Node icon={<CheckIcon />} title="Confidence Threshold" subtitle="Low-confidence rules soft-apply" color={C.guard} sx={{ width: '100%' }} />
              <Node icon={<SyncIcon />} title="Fanout Cap" subtitle="Limits parallel operations" color={C.guard} sx={{ width: '100%' }} />
              <Node icon={<StorageIcon />} title="Daily Cron Cleanup" subtitle="Stale episodic records pruned" color={C.guard} sx={{ width: '100%' }} />
            </Stack>
          </Group>

        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.exec} label="result" /></Stack>

        {/* Improved Execution */}
        <Stack alignItems="center" sx={{ mb: 1 }}>
          <Node icon={<SmartToyIcon />} title="AI Executes with Learned Behavior" subtitle="Improvement is permanent and cumulative" color={C.exec} variant="highlight" sx={{ minWidth: 320 }} />
        </Stack>

        {/* Loop back arrow */}
        <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ color: C.feedback, fontStyle: 'italic', textAlign: 'center' }}>
            ↺ Feedback on this execution re-enters the loop → continuous improvement
          </Typography>
        </Stack>

        {/* Legend */}
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }} justifyContent="center">
          <LegendItem color={C.feedback} label="Feedback Sources" />
          <LegendItem color={C.bridge} label="Via Bridge" />
          <LegendItem color={C.extract} label="LLM Extraction" />
          <LegendItem color={C.store} label="Vector Storage" />
          <LegendItem color={C.inject} label="Context Injection" />
          <LegendItem color={C.guard} label="Safety Guardrails" />
        </Stack>
      </DiagramCanvas>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Diagram 5: Audit Trail
// ─────────────────────────────────────────────

function AuditTrailDiagram() {
  const C = {
    events: '#1565c0',
    logger: '#6a1b9a',
    storage: '#2e7d32',
    access: '#e65100',
    proof: '#00838f',
  };

  const taskEvents = ['Task Created', 'Planning Started', 'Agent Launched', 'Execution Progress', 'Task Completed / Failed'];
  const memoryEvents = ['Memory Recalled', 'New Memory Written', 'Reinforcement Indexed', 'Stale Records Pruned'];
  const feedbackEvents = ['@via Comment Captured', 'Rule Extracted', 'Rule Indexed', 'PR Review Recorded'];
  const systemEvents = ['Status Transition', 'License Reserved', 'AI Employee Provisioned', 'Team Binding Created'];

  return (
    <Box>
      <DiagramHeader
        title="Audit Trail — Complete Observability & Compliance"
        description="Every AI Employee action is immutably logged. Authorized users can inspect the full decision history, view Proof of Execution records, and satisfy compliance requirements."
      />
      <DiagramCanvas>

        {/* Event Categories */}
        <Group title="All Auditable Events" color={C.events} sx={{ mb: 0 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap" gap={2}>

            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography variant="caption" fontWeight={700} color={C.events} sx={{ display: 'block', mb: 1, fontSize: '0.68rem', textTransform: 'uppercase' }}>Task Events</Typography>
              <Stack spacing={0.5}>
                {taskEvents.map(e => (
                  <Typography key={e} variant="caption" sx={{ fontSize: '0.7rem', color: 'text.primary' }}>▸ {e}</Typography>
                ))}
              </Stack>
            </Box>

            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography variant="caption" fontWeight={700} color={C.events} sx={{ display: 'block', mb: 1, fontSize: '0.68rem', textTransform: 'uppercase' }}>Memory Events</Typography>
              <Stack spacing={0.5}>
                {memoryEvents.map(e => (
                  <Typography key={e} variant="caption" sx={{ fontSize: '0.7rem', color: 'text.primary' }}>▸ {e}</Typography>
                ))}
              </Stack>
            </Box>

            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography variant="caption" fontWeight={700} color={C.events} sx={{ display: 'block', mb: 1, fontSize: '0.68rem', textTransform: 'uppercase' }}>Feedback Events</Typography>
              <Stack spacing={0.5}>
                {feedbackEvents.map(e => (
                  <Typography key={e} variant="caption" sx={{ fontSize: '0.7rem', color: 'text.primary' }}>▸ {e}</Typography>
                ))}
              </Stack>
            </Box>

            <Box sx={{ flex: 1, minWidth: 160 }}>
              <Typography variant="caption" fontWeight={700} color={C.events} sx={{ display: 'block', mb: 1, fontSize: '0.68rem', textTransform: 'uppercase' }}>System Events</Typography>
              <Stack spacing={0.5}>
                {systemEvents.map(e => (
                  <Typography key={e} variant="caption" sx={{ fontSize: '0.7rem', color: 'text.primary' }}>▸ {e}</Typography>
                ))}
              </Stack>
            </Box>

          </Stack>
        </Group>

        <Stack alignItems="center"><ArrowDown color={C.logger} label="streamed to" /></Stack>

        {/* Audit Logger */}
        <Stack alignItems="center">
          <Node icon={<TimelineIcon />} title="Real-Time Audit Logger" subtitle="Event-driven, zero-loss capture" color={C.logger} variant="highlight" sx={{ minWidth: 300 }} />
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.storage} label="persisted to" /></Stack>

        {/* Storage */}
        <Stack alignItems="center">
          <Node icon={<StorageIcon />} title="Immutable Secure Storage (MongoDB)" subtitle="Timestamped, tamper-evident audit records" color={C.storage} sx={{ minWidth: 300 }} />
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.storage} /></Stack>

        {/* Three output columns */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="stretch">

          <Group title="Timeline View" color={C.access} sx={{ flex: 1 }}>
            <Stack alignItems="center" spacing={1}>
              <Node icon={<TimelineIcon />} title="Full Event Timeline" subtitle="Chronological, filterable" color={C.access} sx={{ width: '100%' }} />
              <ArrowDown color={C.access} />
              <Node icon={<PersonIcon />} title="Authorized Users" subtitle="Manager / Owner / Auditor" color={C.access} sx={{ width: '100%' }} />
              <ArrowDown color={C.access} />
              <Node icon={<CheckIcon />} title="Compliance Reports" subtitle="Exportable evidence" color={C.access} sx={{ width: '100%' }} />
            </Stack>
          </Group>

          <Group title="Decision Audit" color={C.logger} sx={{ flex: 1 }}>
            <Stack alignItems="center" spacing={1}>
              <Node icon={<PsychologyIcon />} title="Decision Trace" subtitle="Why each action was chosen" color={C.logger} sx={{ width: '100%' }} />
              <ArrowDown color={C.logger} />
              <Node icon={<MemoryIcon />} title="Memory Context Used" subtitle="Which memories informed the decision" color={C.logger} sx={{ width: '100%' }} />
              <ArrowDown color={C.logger} />
              <Node icon={<RLIcon />} title="Reinforcement Rules Applied" subtitle="Which rules constrained behavior" color={C.logger} sx={{ width: '100%' }} />
            </Stack>
          </Group>

          <Group title="Proof of Execution" color={C.proof} sx={{ flex: 1 }}>
            <Stack alignItems="center" spacing={1}>
              <Node icon={<LockIcon />} title="Tymeline ID Record" subtitle="Per Epic, per AI Employee" color={C.proof} sx={{ width: '100%' }} />
              <ArrowDown color={C.proof} />
              <Node icon={<CheckIcon />} title="Permanent Contribution Proof" subtitle="Cryptographically linked identity" color={C.proof} sx={{ width: '100%' }} />
              <ArrowDown color={C.proof} />
              <Node icon={<BusinessIcon />} title="Portfolio & Compliance" subtitle="Auditable contribution history" color={C.proof} sx={{ width: '100%' }} />
            </Stack>
          </Group>

        </Stack>

        {/* Legend */}
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }} justifyContent="center">
          <LegendItem color={C.events} label="Event Sources" />
          <LegendItem color={C.logger} label="Audit Logger / Decision Trace" />
          <LegendItem color={C.storage} label="Secure Storage" />
          <LegendItem color={C.access} label="Access & Compliance" />
          <LegendItem color={C.proof} label="Proof of Execution" />
        </Stack>
      </DiagramCanvas>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Diagram 6: AI Employee Chat
// ─────────────────────────────────────────────

function ChatDiagram() {
  const C = {
    user: '#1565c0',
    webrtc: '#6a1b9a',
    signal: '#e65100',
    pipeline: '#2e7d32',
    context: '#00838f',
    response: '#f57c00',
  };

  return (
    <Box>
      <DiagramHeader
        title="AI Employee Chat — Voice & Text Communication"
        description="How users communicate with AI Employees via real-time voice chat using WebRTC, with two signaling paths: WebSocket (low latency) and REST polling."
      />
      <DiagramCanvas>

        {/* User Input */}
        <Stack alignItems="center">
          <Node icon={<PersonIcon />} title="User Speaks / Types" subtitle="Microphone or text input" color={C.user} variant="highlight" sx={{ minWidth: 260 }} />
          <ArrowDown color={C.webrtc} label="captured via" />

          {/* WebRTC Setup */}
          <Group title="WebRTC Setup" color={C.webrtc} sx={{ maxWidth: 480, width: '100%', mb: 0 }}>
            <Stack alignItems="center" spacing={1.5}>
              <Node icon={<WebRTCIcon />} title="RTCPeerConnection Created" subtitle="ICE servers configured" color={C.webrtc} sx={{ minWidth: 250 }} />
              <ArrowDown color={C.webrtc} />
              <Node icon={<STTIcon />} title="Media Stream Acquired" subtitle="User microphone → audio track" color={C.webrtc} sx={{ minWidth: 250 }} />
            </Stack>
          </Group>

          <ArrowDown color={C.signal} label="negotiated via signaling channel" />
        </Stack>

        {/* Two signaling paths */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="center" alignItems="flex-start">

          <Group title="WebSocket Path (Socket.IO)" color={C.signal} sx={{ flex: 1 }}>
            <Stack alignItems="center" spacing={0}>
              <Node icon={<HubIcon />} title="Socket.IO Connection" subtitle="Real-time, bidirectional" color={C.signal} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.signal} />
              <Node icon={<CheckIcon />} title="Auth Handshake" subtitle="Identity verified" color={C.signal} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.signal} />
              <Node icon={<SyncIcon />} title="WebRTC Offer/Answer" subtitle="Via WebSocket events" color={C.signal} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.signal} />
              <Node icon={<HubIcon />} title="ICE Candidate Exchange" subtitle="NAT traversal negotiated" color={C.signal} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.signal} />
              <Node icon={<CheckIcon />} title="Audio Stream Ready" subtitle="✓ Low latency" color={C.signal} sx={{ minWidth: 180 }} />
            </Stack>
          </Group>

          <Group title="REST Polling Path" color={C.response} sx={{ flex: 1 }}>
            <Stack alignItems="center" spacing={0}>
              <Node icon={<HubIcon />} title="POST /voice/start" subtitle="Session initialized" color={C.response} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.response} />
              <Node icon={<SyncIcon />} title="POST /signal/{id}/offer" subtitle="SDP offer sent" color={C.response} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.response} />
              <Node icon={<LoopIcon />} title="GET /signal/{id}/answer" subtitle="Polled every 1000ms" color={C.response} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.response} />
              <Node icon={<HubIcon />} title="ICE via POST /ice" subtitle="Candidates exchanged" color={C.response} sx={{ minWidth: 180 }} />
              <ArrowDown color={C.response} />
              <Node icon={<CheckIcon />} title="Audio Stream Ready" subtitle="✓ Polling fallback" color={C.response} sx={{ minWidth: 180 }} />
            </Stack>
          </Group>

        </Stack>

        {/* Merge - Voice Processing */}
        <Stack alignItems="center" spacing={0} sx={{ mt: 2 }}>
          <ArrowDown color={C.pipeline} label="audio received by server" />

          <Group title="Voice Processing Pipeline (Server Side)" color={C.pipeline} sx={{ maxWidth: 520, width: '100%', mb: 0 }}>
            <Stack alignItems="center" spacing={1.5}>
              <Node icon={<STTIcon />} title="STT — Speech to Text" subtitle="Audio transcribed to text" color={C.pipeline} sx={{ minWidth: 260 }} />
              <ArrowDown color={C.context} />

              <Group title="Context Assembly" color={C.context} sx={{ width: '100%' }}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1} justifyContent="center">
                  <Node icon={<MemoryIcon />} title="Personal Memory" subtitle="Employee history" color={C.context} sx={{ minWidth: 110 }} />
                  <Node icon={<StorageIcon />} title="Project Memory" subtitle="Shared context" color={C.context} sx={{ minWidth: 110 }} />
                  <Node icon={<GuardIcon />} title="Pinned Rules" subtitle="RL constraints" color={C.context} sx={{ minWidth: 110 }} />
                </Stack>
              </Group>

              <ArrowDown color={C.pipeline} />
              <Node icon={<PsychologyIcon />} title="LLM Processing" subtitle="Generates response with full context" color={C.pipeline} variant="highlight" sx={{ minWidth: 260 }} />
              <ArrowDown color={C.pipeline} />
              <Node icon={<TTSIcon />} title="TTS — Text to Speech" subtitle="Response converted to audio" color={C.pipeline} sx={{ minWidth: 260 }} />
            </Stack>
          </Group>

          <ArrowDown color={C.user} label="via WebRTC audio channel" />
          <Node icon={<PersonIcon />} title="User Receives Voice Response" subtitle="Audio played through speaker" color={C.user} variant="highlight" sx={{ minWidth: 300 }} />
        </Stack>

        {/* Legend */}
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }} justifyContent="center">
          <LegendItem color={C.user} label="User" />
          <LegendItem color={C.webrtc} label="WebRTC Setup" />
          <LegendItem color={C.signal} label="WebSocket Signaling" />
          <LegendItem color={C.response} label="REST Signaling" />
          <LegendItem color={C.pipeline} label="Voice Pipeline" />
          <LegendItem color={C.context} label="Context Assembly" />
        </Stack>
      </DiagramCanvas>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Diagram 7: Kill Switch
// ─────────────────────────────────────────────

function KillSwitchDiagram() {
  const C = {
    types:    '#b71c1c',
    detect:   '#6a1b9a',
    halt:     '#c62828',
    action:   '#e65100',
    review:   '#1565c0',
    recovery: '#2e7d32',
    scope:    '#37474f',
  };

  const scopeRows = [
    { scope: 'global',       effect: 'All AI Employee executions across the entire business' },
    { scope: 'ai_employee',  effect: 'One specific AI Employee, all tasks' },
    { scope: 'project',      effect: 'All AI Employees assigned to a specific project' },
    { scope: 'task_type',    effect: 'Tasks of a particular category (coding, HR, reporting…)' },
    { scope: 'agent',        effect: 'A specific agent type, e.g., block all Cursor calls' },
  ];

  const chipSx = (color) => ({
    backgroundColor: `${color}15`,
    border: `1px solid ${color}60`,
    fontWeight: 600,
    fontSize: '0.68rem',
    color,
  });

  return (
    <Box>
      <DiagramHeader
        title="Kill Switch — Safety Halt Mechanism"
        description="Kill switches are the highest-priority safety guardrail. When triggered they immediately halt AI Employee execution, preserve all partial work, notify the responsible manager, and require explicit human acknowledgement before any resumption."
      />

      {/* Alert banner */}
      <Box sx={{ mb: 3, p: 1.5, border: `1px solid ${C.halt}`, borderRadius: 1.5, backgroundColor: '#ffebee', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <RiskIcon sx={{ color: C.halt, fontSize: 20, flexShrink: 0, mt: 0.1 }} />
        <Typography variant="caption" sx={{ color: C.halt, fontWeight: 600, lineHeight: 1.5 }}>
          Kill switch rules are absolute — they override all other reinforcement rules, memory context, and LLM instructions. Once fired, no execution proceeds without explicit human override.
        </Typography>
      </Box>

      <DiagramCanvas>

        {/* ── Trigger Sources ── */}
        <Group title="Kill Switch Trigger Sources" color={C.types}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" flexWrap="wrap" gap={1.5}>
            <Node icon={<MemoryIcon />} title="RL Rule" subtitle='Rule flagged kill_switch=true in MongoDB' color={C.types} sx={{ minWidth: 155 }} />
            <Node icon={<AdminIcon />} title="Admin / Manager" subtitle="Manual trigger from Tymeline Dashboard" color={C.types} sx={{ minWidth: 155 }} />
            <Node icon={<GuardIcon />} title="Auto Safety Monitor" subtitle="Fanout cap or token budget exceeded" color={C.types} sx={{ minWidth: 155 }} />
            <Node icon={<RiskIcon />} title="Confidence Gate" subtitle="Risk score too high, confidence too low" color={C.types} sx={{ minWidth: 155 }} />
          </Stack>
        </Group>

        <Stack alignItems="center"><ArrowDown color={C.detect} label="checked at" /></Stack>

        {/* ── Detection ── */}
        <Stack alignItems="center">
          <Group title="Detection Point — Execution Planning Phase (before any agent launches)" color={C.detect} sx={{ maxWidth: 560, width: '100%' }}>
            <Stack spacing={1.5} alignItems="center">
              <Node icon={<PsychologyIcon />} title="Kill Switch Evaluator" subtitle="Runs before LLM plan is built" color={C.detect} sx={{ minWidth: 290 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} justifyContent="center">
                <Chip label="Rule scope ∩ current context?" size="small" sx={chipSx(C.detect)} />
                <Chip label="kill_switch flag set?" size="small" sx={chipSx(C.detect)} />
                <Chip label="Threshold crossed?" size="small" sx={chipSx(C.detect)} />
              </Stack>
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.halt} label="condition = TRUE" /></Stack>

        {/* ── HALTED state ── */}
        <Stack alignItems="center">
          <Node icon={<HaltIcon />} title="TASK HALTED" subtitle='Status → "halted"  •  No further agent calls' color={C.halt} variant="highlight" sx={{ minWidth: 320 }} />
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.action} /></Stack>

        {/* ── Immediate actions ── */}
        <Group title="Immediate Automatic Actions" color={C.action} sx={{ mb: 0 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" flexWrap="wrap" gap={1.5}>
            <Node icon={<BlockIcon />} title="Agents Terminated" subtitle="All active agents stopped" color={C.action} sx={{ minWidth: 130 }} />
            <Node icon={<StorageIcon />} title="Partial Work Preserved" subtitle="Not discarded — saved for review" color={C.action} sx={{ minWidth: 130 }} />
            <Node icon={<AuditIcon />} title="Audit Log Entry" subtitle="Kill switch event recorded" color={C.action} sx={{ minWidth: 130 }} />
            <Node icon={<NotifyIcon />} title="Manager Notified" subtitle="Alert with reason + context" color={C.action} sx={{ minWidth: 130 }} />
          </Stack>
        </Group>

        <Stack alignItems="center"><ArrowDown color={C.review} label="requires human review" /></Stack>

        {/* ── Human Review ── */}
        <Stack alignItems="center">
          <Node icon={<PersonIcon />} title="Human Review — Tymeline Dashboard" subtitle="Manager sees: trigger reason  •  execution context  •  partial work  •  suggested resolution" color={C.review} variant="highlight" sx={{ minWidth: 380 }} />
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.review} /></Stack>

        {/* ── Three decisions ── */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="stretch">

          <Group title="Decision A — Override &amp; Resume" color={C.recovery} sx={{ flex: 1 }}>
            <Stack spacing={1} alignItems="center">
              <Node icon={<CheckIcon />} title="Manager Acknowledges" subtitle="Reviewed and accepts the risk" color={C.recovery} sx={{ width: '100%' }} />
              <ArrowDown color={C.recovery} />
              <Node icon={<TriggerIcon />} title="Task Re-queued" subtitle="Returns to pending state" color={C.recovery} sx={{ width: '100%' }} />
              <ArrowDown color={C.recovery} />
              <Node icon={<AuditIcon />} title="Override Recorded" subtitle="Audit trail entry: acknowledged by [user]" color={C.recovery} sx={{ width: '100%' }} />
            </Stack>
          </Group>

          <Group title="Decision B — Update Rule" color={C.action} sx={{ flex: 1 }}>
            <Stack spacing={1} alignItems="center">
              <Node icon={<EditRoleIcon />} title="Modify Kill Switch Rule" subtitle="Adjust scope, confidence threshold, or condition" color={C.action} sx={{ width: '100%' }} />
              <ArrowDown color={C.action} />
              <Node icon={<StorageIcon />} title="Rule Updated in MongoDB" subtitle="New version indexed as vector" color={C.action} sx={{ width: '100%' }} />
              <ArrowDown color={C.action} />
              <Node icon={<TriggerIcon />} title="Task Re-queued" subtitle="Runs under updated constraints" color={C.action} sx={{ width: '100%' }} />
            </Stack>
          </Group>

          <Group title="Decision C — Disable &amp; Escalate" color={C.halt} sx={{ flex: 1 }}>
            <Stack spacing={1} alignItems="center">
              <Node icon={<BlockIcon />} title="Kill Switch Deactivated" subtitle="Rule removed or set inactive" color={C.halt} sx={{ width: '100%' }} />
              <ArrowDown color={C.halt} />
              <Node icon={<AdminIcon />} title="Escalate to Org Admin" subtitle="If security incident suspected" color={C.halt} sx={{ width: '100%' }} />
              <ArrowDown color={C.halt} />
              <Node icon={<AuditIcon />} title="Deactivation Logged" subtitle="Permanent audit record" color={C.halt} sx={{ width: '100%' }} />
            </Stack>
          </Group>

        </Stack>

        {/* ── Scope Table ── */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" fontWeight={700} sx={{ color: C.scope, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.68rem', display: 'block', mb: 1.5 }}>
            Kill Switch Scope Levels
          </Typography>
          <Box sx={{ border: `1px solid ${C.scope}30`, borderRadius: 1.5, overflow: 'hidden' }}>
            {/* header */}
            <Stack direction="row" sx={{ backgroundColor: `${C.scope}12`, borderBottom: `1px solid ${C.scope}30`, px: 2, py: 0.8 }}>
              <Typography variant="caption" fontWeight={700} sx={{ color: C.scope, width: 130, fontSize: '0.68rem', textTransform: 'uppercase' }}>Scope</Typography>
              <Typography variant="caption" fontWeight={700} sx={{ color: C.scope, flex: 1, fontSize: '0.68rem', textTransform: 'uppercase' }}>Effect</Typography>
            </Stack>
            {scopeRows.map((row, i) => (
              <Stack key={row.scope} direction="row" sx={{ px: 2, py: 0.8, borderBottom: i < scopeRows.length - 1 ? `1px solid ${C.scope}20` : 'none', '&:hover': { backgroundColor: `${C.scope}06` } }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: C.types, fontWeight: 700, width: 130, fontSize: '0.72rem' }}>{row.scope}</Typography>
                <Typography variant="caption" sx={{ color: 'text.primary', flex: 1, fontSize: '0.72rem' }}>{row.effect}</Typography>
              </Stack>
            ))}
          </Box>
        </Box>

        {/* Legend */}
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }} justifyContent="center">
          <LegendItem color={C.types}    label="Trigger Sources" />
          <LegendItem color={C.detect}   label="Detection Phase" />
          <LegendItem color={C.halt}     label="Halted State" />
          <LegendItem color={C.action}   label="Automatic Actions" />
          <LegendItem color={C.review}   label="Human Review" />
          <LegendItem color={C.recovery} label="Recovery" />
        </Stack>
      </DiagramCanvas>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Diagram 8: Dynamic Role Creation (New Feature)
// ─────────────────────────────────────────────

function DynamicRoleCreationDiagram() {
  const C = {
    input:   '#1565c0',
    llm:     '#6a1b9a',
    agents:  '#e65100',
    gap:     '#f57c00',
    valid:   '#00838f',
    config:  '#2e7d32',
    library: '#4527a0',
  };

  const agentLibrary = [
    { name: 'Coder Agent',         tools: 'Cursor, GitHub',        icon: <CodeIcon sx={{ fontSize: 14 }} /> },
    { name: 'HR Agent',            tools: 'ATS, HRIS, Job Boards', icon: <AgentIcon sx={{ fontSize: 14 }} /> },
    { name: 'Research Agent',      tools: 'Web Search, Docs',      icon: <ScanIcon sx={{ fontSize: 14 }} /> },
    { name: 'Communication Agent', tools: 'SMTP, Slack, Calendar', icon: <NotifyIcon sx={{ fontSize: 14 }} /> },
    { name: 'Data Agent',          tools: 'SQL, Python, Sheets',   icon: <AnalyticsIcon sx={{ fontSize: 14 }} /> },
    { name: 'Review Agent',        tools: 'Code Review, Docs',     icon: <CheckIcon sx={{ fontSize: 14 }} /> },
  ];

  const exampleRoles = [
    { role: 'DevOps Engineer',   agents: ['Coder Agent', 'Communication Agent', 'Research Agent'] },
    { role: 'Data Analyst',      agents: ['Data Agent', 'Research Agent', 'Communication Agent'] },
    { role: 'Legal Reviewer',    agents: ['Review Agent', 'Research Agent', 'Communication Agent'] },
    { role: 'QA Engineer',       agents: ['Coder Agent', 'Review Agent', 'Research Agent'] },
  ];

  return (
    <Box>
      <DiagramHeader
        title="Dynamic Role Creation — Custom AI Employee Roles"
        description="[Proposed Feature] Today roles are predefined. Dynamic Role Creation lets businesses describe any role in natural language; the system automatically maps it to existing agents, performs a gap analysis, and generates a reusable role configuration."
      />

      {/* Feature badge */}
      <Box sx={{ mb: 3, p: 1.5, border: `1px solid ${C.llm}`, borderRadius: 1.5, backgroundColor: `${C.llm}08`, display: 'flex', gap: 1, alignItems: 'center' }}>
        <DynamicIcon sx={{ color: C.llm, fontSize: 20, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: C.llm, fontWeight: 600, lineHeight: 1.5 }}>
          New Feature Design — builds on the existing LLM-based role auto-fill and agent binding infrastructure already in the platform.
        </Typography>
      </Box>

      <DiagramCanvas>

        {/* ── Input ── */}
        <Stack alignItems="center">
          <Group title="User Input" color={C.input} sx={{ maxWidth: 540, width: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" gap={1.5}>
              <Node icon={<EditRoleIcon />} title="Role Name" subtitle='e.g. "DevOps Engineer"' color={C.input} sx={{ flex: 1 }} />
              <Node icon={<TaskIcon />} title="Responsibilities" subtitle="Key duties described in plain text" color={C.input} sx={{ flex: 1 }} />
              <Node icon={<LibraryIcon />} title="Optional Template" subtitle="Start from an existing role" color={C.input} sx={{ flex: 1 }} />
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.llm} label="analyzed by" /></Stack>

        {/* ── LLM Role Analyzer ── */}
        <Stack alignItems="center">
          <Group title="LLM Role Analyzer" color={C.llm} sx={{ maxWidth: 560, width: '100%' }}>
            <Stack spacing={1.5} alignItems="center">
              <Node icon={<PsychologyIcon />} title="Semantic Role Analysis" subtitle="Extracts structured requirements from description" color={C.llm} sx={{ minWidth: 300 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} justifyContent="center">
                <Chip label="Core capabilities needed" size="small" sx={{ backgroundColor: `${C.llm}15`, border: `1px solid ${C.llm}60`, fontSize: '0.67rem', fontWeight: 600, color: C.llm }} />
                <Chip label="Tool / integration needs" size="small" sx={{ backgroundColor: `${C.llm}15`, border: `1px solid ${C.llm}60`, fontSize: '0.67rem', fontWeight: 600, color: C.llm }} />
                <Chip label="Async or sync execution?" size="small" sx={{ backgroundColor: `${C.llm}15`, border: `1px solid ${C.llm}60`, fontSize: '0.67rem', fontWeight: 600, color: C.llm }} />
                <Chip label="Data access requirements" size="small" sx={{ backgroundColor: `${C.llm}15`, border: `1px solid ${C.llm}60`, fontSize: '0.67rem', fontWeight: 600, color: C.llm }} />
              </Stack>
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.agents} label="matched against" /></Stack>

        {/* ── Agent Library ── */}
        <Group title="Agent Library (Existing)" color={C.agents} sx={{ mb: 0 }}>
          <Stack direction="row" flexWrap="wrap" gap={1.5} justifyContent="center">
            {agentLibrary.map(a => (
              <Box key={a.name} sx={{ border: `1.5px solid ${C.agents}`, borderRadius: 1.5, px: 1.5, py: 1, backgroundColor: `${C.agents}08`, minWidth: 140, textAlign: 'center' }}>
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center" sx={{ mb: 0.3 }}>
                  <Box sx={{ color: C.agents }}>{a.icon}</Box>
                  <Typography variant="caption" fontWeight={700} sx={{ color: C.agents, fontSize: '0.7rem' }}>{a.name}</Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.62rem' }}>{a.tools}</Typography>
              </Box>
            ))}
          </Stack>
        </Group>

        <Stack alignItems="center"><ArrowDown color={C.gap} /></Stack>

        {/* ── Gap Analysis ── */}
        <Stack alignItems="center">
          <Group title="Gap Analysis" color={C.gap} sx={{ maxWidth: 560, width: '100%' }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckIcon sx={{ color: C.config, fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.primary' }}>
                  <strong>Matched</strong> — required capability covered by an existing agent → auto-bind
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <GuardIcon sx={{ color: C.gap, fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.primary' }}>
                  <strong>Partial</strong> — agent covers most needs but missing a tool → flag for manual configuration
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <RiskIcon sx={{ color: C.llm, fontSize: 16 }} />
                <Typography variant="caption" sx={{ fontSize: '0.72rem', color: 'text.primary' }}>
                  <strong>Gap</strong> — no existing agent covers this need → request new agent or mark as unsupported capability
                </Typography>
              </Stack>
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.valid} label="validated by" /></Stack>

        {/* ── Validation ── */}
        <Stack alignItems="center">
          <Group title="Pre-Creation Validation" color={C.valid} sx={{ maxWidth: 500, width: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" gap={1}>
              <Node icon={<ExtensionIcon />} title="Integrations Connected?" subtitle="All required tools are linked" color={C.valid} sx={{ flex: 1 }} />
              <Node icon={<LockIcon />} title="License Available?" subtitle="AI Employee slot free" color={C.valid} sx={{ flex: 1 }} />
              <Node icon={<CheckIcon />} title="Capability Preview" subtitle="Show what role can / cannot do" color={C.valid} sx={{ flex: 1 }} />
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.config} /></Stack>

        {/* ── Config Generated ── */}
        <Stack alignItems="center">
          <Group title="Role Configuration Generated" color={C.config} sx={{ maxWidth: 540, width: '100%' }}>
            <Stack direction="row" flexWrap="wrap" gap={1.5} justifyContent="center">
              <Node icon={<TreeIcon />} title="role_config" subtitle="Capabilities + execution pattern" color={C.config} sx={{ minWidth: 130 }} />
              <Node icon={<AgentIcon />} title="agent_ids" subtitle="All bound agent references" color={C.config} sx={{ minWidth: 130 }} />
              <Node icon={<MemoryIcon />} title="memory_template" subtitle="Initial context seed" color={C.config} sx={{ minWidth: 130 }} />
              <Node icon={<SyncIcon />} title="execution_pattern" subtitle="Async / sync, fanout cap" color={C.config} sx={{ minWidth: 130 }} />
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.library} /></Stack>

        {/* ── Role Library ── */}
        <Stack alignItems="center">
          <Node icon={<LibraryIcon />} title="Saved to Role Library" subtitle="Reusable  •  Version controlled  •  Shareable across org" color={C.library} variant="highlight" sx={{ minWidth: 340 }} />
        </Stack>

        {/* ── Role → Agent mapping examples ── */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="caption" fontWeight={700} sx={{ color: C.llm, textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.68rem', display: 'block', mb: 1.5 }}>
            Example: LLM Role → Agent Auto-Binding
          </Typography>
          <Stack spacing={1}>
            {exampleRoles.map(r => (
              <Stack key={r.role} direction="row" alignItems="center" spacing={1.5} sx={{ p: 1, border: `1px solid ${C.llm}25`, borderRadius: 1.5, backgroundColor: `${C.llm}05` }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: C.llm, width: 150, flexShrink: 0, fontSize: '0.72rem' }}>{r.role}</Typography>
                <Typography variant="caption" sx={{ color: C.agents, fontSize: '0.7rem' }}>→</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                  {r.agents.map(a => (
                    <Chip key={a} label={a} size="small" sx={{ fontSize: '0.62rem', backgroundColor: `${C.agents}12`, border: `1px solid ${C.agents}50` }} />
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Box>

        {/* Legend */}
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }} justifyContent="center">
          <LegendItem color={C.input}   label="User Input" />
          <LegendItem color={C.llm}     label="LLM Analysis" />
          <LegendItem color={C.agents}  label="Agent Library" />
          <LegendItem color={C.gap}     label="Gap Analysis" />
          <LegendItem color={C.valid}   label="Validation" />
          <LegendItem color={C.config}  label="Role Config" />
          <LegendItem color={C.library} label="Role Library" />
        </Stack>
      </DiagramCanvas>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Diagram 9: GitHub Repo → AI Employee Recommender (New Feature)
// ─────────────────────────────────────────────

function GitHubRepoAnalysisDiagram() {
  const C = {
    input:    '#1565c0',
    api:      '#6a1b9a',
    llm:      '#e65100',
    infer:    '#f57c00',
    match:    '#2e7d32',
    partial:  '#f57c00',
    create:   '#c62828',
    output:   '#00838f',
  };

  const apiCalls = [
    { endpoint: 'GET /repos/{owner}/{repo}',           purpose: 'Metadata: description, topics, visibility' },
    { endpoint: 'GET /repos/{owner}/{repo}/languages',  purpose: 'Language breakdown (Python 60%, JS 30%…)' },
    { endpoint: 'GET /repos/{owner}/{repo}/contents',   purpose: 'File tree: Dockerfile, terraform/, tests/…' },
    { endpoint: 'GET /repos/{owner}/{repo}/pulls',      purpose: 'PR velocity, open count, review patterns' },
    { endpoint: 'GET /repos/{owner}/{repo}/issues',     purpose: 'Issue categories, backlog volume' },
    { endpoint: 'GET /repos/{owner}/{repo}/stats/contributors', purpose: 'Team size & contribution density' },
  ];

  const inferenceRules = [
    { signal: 'Python / ML libraries (torch, sklearn)', role: 'ML / Data Scientist Engineer' },
    { signal: 'React / Vue / Next.js files', role: 'Frontend Engineer' },
    { signal: 'Dockerfile / terraform / .github/workflows', role: 'DevOps / Platform Engineer' },
    { signal: 'Low test coverage detected', role: 'QA / Test Automation Engineer' },
    { signal: 'High open issue count (>50)', role: 'Project / Triage Manager' },
    { signal: 'Backend API (FastAPI / Express / Django)', role: 'Backend Engineer' },
  ];

  return (
    <Box>
      <DiagramHeader
        title="GitHub Repo Analyzer — AI Employee Recommender"
        description="[Proposed Feature] Input a GitHub repository URL and Tymeline automatically analyzes the codebase to infer which AI Employee roles are needed, matches against existing employees, and suggests assignment or creation."
      />

      {/* Feature badge */}
      <Box sx={{ mb: 3, p: 1.5, border: `1px solid ${C.api}`, borderRadius: 1.5, backgroundColor: `${C.api}08`, display: 'flex', gap: 1, alignItems: 'center' }}>
        <DynamicIcon sx={{ color: C.api, fontSize: 20, flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: C.api, fontWeight: 600, lineHeight: 1.5 }}>
          New Feature Design — extends the existing GitHub integration (currently PR creation + @via feedback) to full repo-level intelligence and workforce planning.
        </Typography>
      </Box>

      <DiagramCanvas>

        {/* ── Input ── */}
        <Stack alignItems="center">
          <Group title="Input" color={C.input} sx={{ maxWidth: 500, width: '100%' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" gap={1.5}>
              <Node icon={<GitHubIcon />} title="GitHub Repo URL" subtitle="github.com/org/repo-name" color={C.input} sx={{ flex: 1 }} />
              <Node icon={<LockIcon />} title="GitHub Token" subtitle="OAuth or PAT with read access" color={C.input} sx={{ flex: 1 }} />
              <Node icon={<ScanIcon />} title="Analysis Depth" subtitle="Quick scan or Full analysis" color={C.input} sx={{ flex: 1 }} />
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.api} label="calls GitHub API" /></Stack>

        {/* ── GitHub API Analysis ── */}
        <Stack alignItems="center">
          <Group title="GitHub API — Repo Intelligence Gathering" color={C.api} sx={{ maxWidth: 600, width: '100%' }}>
            <Stack spacing={0.8}>
              {apiCalls.map(a => (
                <Stack key={a.endpoint} direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 0.8, borderRadius: 1, '&:hover': { backgroundColor: `${C.api}06` } }}>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: C.api, fontWeight: 700, fontSize: '0.66rem', width: 280, flexShrink: 0 }}>{a.endpoint}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.67rem' }}>{a.purpose}</Typography>
                </Stack>
              ))}
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.llm} label="passed to LLM" /></Stack>

        {/* ── LLM Inference ── */}
        <Stack alignItems="center">
          <Group title="LLM Role Inference Engine" color={C.llm} sx={{ maxWidth: 560, width: '100%' }}>
            <Stack spacing={1.5} alignItems="center">
              <Node icon={<PsychologyIcon />} title={'"Given this codebase, what AI Employee roles are needed?"'} subtitle="LLM receives structured repo analysis as context" color={C.llm} sx={{ minWidth: 320 }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: C.llm, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.8 }}>Signal → Role Mapping Examples</Typography>
              <Stack spacing={0.5} sx={{ width: '100%' }}>
                {inferenceRules.map(r => (
                  <Stack key={r.signal} direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5, borderRadius: 1, backgroundColor: `${C.llm}08` }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem', flex: 1 }}>{r.signal}</Typography>
                    <Typography variant="caption" sx={{ color: C.llm, fontSize: '0.65rem', flexShrink: 0 }}>→</Typography>
                    <Chip label={r.role} size="small" sx={{ fontSize: '0.62rem', backgroundColor: `${C.llm}15`, border: `1px solid ${C.llm}50`, color: C.llm, fontWeight: 600, flexShrink: 0 }} />
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.infer} label="produces" /></Stack>

        {/* ── Inferred Requirements ── */}
        <Stack alignItems="center">
          <Group title="Inferred Role Requirements List" color={C.infer} sx={{ maxWidth: 500, width: '100%' }}>
            <Stack alignItems="center" spacing={1.5}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', textAlign: 'center' }}>
                Example output for a FastAPI + React + Docker repo:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} justifyContent="center">
                {['Backend Engineer', 'Frontend Engineer', 'DevOps Engineer', 'QA Engineer'].map(r => (
                  <Chip key={r} label={r} size="small" sx={{ fontWeight: 700, backgroundColor: `${C.infer}15`, border: `1px solid ${C.infer}`, color: C.infer }} />
                ))}
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>+ rationale for each recommendation</Typography>
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.match} label="compared against" /></Stack>

        {/* ── Matching Engine ── */}
        <Stack alignItems="center">
          <Node icon={<MatchIcon />} title="AI Employee Matching Engine" subtitle="Inferred needs  vs.  existing AI Employees in this business" color={C.match} sx={{ minWidth: 360 }} />
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.match} /></Stack>

        {/* ── Three outcome columns ── */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems="stretch">

          <Group title="✓ Match Found" color={C.match} sx={{ flex: 1 }}>
            <Stack spacing={1} alignItems="center">
              <Node icon={<SmartToyIcon />} title="Existing AI Employee" subtitle="Role + capability fit confirmed" color={C.match} sx={{ width: '100%' }} />
              <ArrowDown color={C.match} />
              <Node icon={<CheckIcon />} title="Suggest Assignment" subtitle="One-click: add to this project" color={C.match} sx={{ width: '100%' }} />
              <ArrowDown color={C.match} />
              <Node icon={<TimelineIcon />} title="Availability Checked" subtitle="Not over-allocated on other projects" color={C.match} sx={{ width: '100%' }} />
            </Stack>
          </Group>

          <Group title="~ Partial Match" color={C.partial} sx={{ flex: 1 }}>
            <Stack spacing={1} alignItems="center">
              <Node icon={<SmartToyIcon />} title="Similar AI Employee" subtitle="Role matches, missing some tools" color={C.partial} sx={{ width: '100%' }} />
              <ArrowDown color={C.partial} />
              <Node icon={<EditRoleIcon />} title="Suggest Modification" subtitle="Add missing agents to existing employee" color={C.partial} sx={{ width: '100%' }} />
              <ArrowDown color={C.partial} />
              <Node icon={<ExtensionIcon />} title="Dynamic Role Update" subtitle="Uses Dynamic Role Creation flow" color={C.partial} sx={{ width: '100%' }} />
            </Stack>
          </Group>

          <Group title="✗ No Match" color={C.create} sx={{ flex: 1 }}>
            <Stack spacing={1} alignItems="center">
              <Node icon={<RiskIcon />} title="No Suitable Employee" subtitle="Role type not yet in roster" color={C.create} sx={{ width: '100%' }} />
              <ArrowDown color={C.create} />
              <Node icon={<AddIcon />} title="Create New AI Employee" subtitle="Pre-filled from repo analysis" color={C.create} sx={{ width: '100%' }} />
              <ArrowDown color={C.create} />
              <Node icon={<DynamicIcon />} title="Dynamic Role Creation Flow" subtitle="Repo context seeds role config" color={C.create} sx={{ width: '100%' }} />
            </Stack>
          </Group>

        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.output} label="presented as" /></Stack>

        {/* ── Recommendation Output ── */}
        <Stack alignItems="center">
          <Group title="Recommendation Output — Dashboard View" color={C.output} sx={{ maxWidth: 600, width: '100%' }}>
            <Stack spacing={1.5} alignItems="center">
              <Node icon={<RecommendIcon />} title="Recommendation Cards" subtitle="One card per inferred role need" color={C.output} sx={{ minWidth: 300 }} />
              <Stack direction="row" spacing={2} flexWrap="wrap" gap={1.5} justifyContent="center">
                <Node icon={<CheckIcon />} title="Role + Reason" subtitle="Why this role is needed" color={C.output} sx={{ minWidth: 130 }} />
                <Node icon={<SmartToyIcon />} title="Matched Employee" subtitle="Or 'Create New' option" color={C.output} sx={{ minWidth: 130 }} />
                <Node icon={<TriggerIcon />} title="One-Click Action" subtitle="Assign / Modify / Create" color={C.output} sx={{ minWidth: 130 }} />
              </Stack>
            </Stack>
          </Group>
        </Stack>

        <Stack alignItems="center"><ArrowDown color={C.output} /></Stack>

        {/* ── Final actions ── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" flexWrap="wrap" gap={1.5}>
          <Node icon={<TreeIcon />} title="Project Assignment" subtitle="Assign to project immediately" color={C.match} sx={{ minWidth: 150 }} />
          <Node icon={<BuildIcon />} title="Modify Existing" subtitle="Agent binding updated" color={C.partial} sx={{ minWidth: 150 }} />
          <Node icon={<DynamicIcon />} title="Dynamic Role Creation" subtitle="New AI Employee provisioned" color={C.create} sx={{ minWidth: 150 }} />
        </Stack>

        {/* Legend */}
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }} justifyContent="center">
          <LegendItem color={C.input}   label="Input" />
          <LegendItem color={C.api}     label="GitHub API" />
          <LegendItem color={C.llm}     label="LLM Inference" />
          <LegendItem color={C.match}   label="Match Found" />
          <LegendItem color={C.partial} label="Partial Match" />
          <LegendItem color={C.create}  label="No Match / Create" />
          <LegendItem color={C.output}  label="Recommendation" />
        </Stack>
      </DiagramCanvas>
    </Box>
  );
}

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────

const DIAGRAMS = [
  {
    label: 'Product Overview',
    icon: <SmartToyIcon sx={{ fontSize: 18 }} />,
    component: ProductOverviewDiagram,
  },
  {
    label: 'Task Execution',
    icon: <PlayIcon sx={{ fontSize: 18 }} />,
    component: TaskExecutionDiagram,
  },
  {
    label: 'Activity Management',
    icon: <TimelineIcon sx={{ fontSize: 18 }} />,
    component: ActivityManagementDiagram,
  },
  {
    label: 'Reinforcement Loop',
    icon: <RLIcon sx={{ fontSize: 18 }} />,
    component: ReinforcementDiagram,
  },
  {
    label: 'Audit Trail',
    icon: <AuditIcon sx={{ fontSize: 18 }} />,
    component: AuditTrailDiagram,
  },
  {
    label: 'AI Employee Chat',
    icon: <ChatIcon sx={{ fontSize: 18 }} />,
    component: ChatDiagram,
  },
  {
    label: 'Kill Switch',
    icon: <BlockIcon sx={{ fontSize: 18 }} />,
    component: KillSwitchDiagram,
  },
  {
    label: 'Dynamic Role Creation',
    icon: <DynamicIcon sx={{ fontSize: 18 }} />,
    component: DynamicRoleCreationDiagram,
  },
  {
    label: 'GitHub Repo Analyzer',
    icon: <GitHubIcon sx={{ fontSize: 18 }} />,
    component: GitHubRepoAnalysisDiagram,
  },
];

export default function AIEmployeeProductDiagram() {
  const [activeTab, setActiveTab] = useState(0);

  const ActiveDiagram = DIAGRAMS[activeTab].component;

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Stack direction="row" justifyContent="center" spacing={1.5} sx={{ mb: 1.5 }} alignItems="center">
          <SmartToyIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h3" fontWeight={800} sx={{ color: 'text.primary', letterSpacing: '-0.02em' }}>
            AI Employee
          </Typography>
        </Stack>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 1.5, color: 'primary.main' }}>
          Product Diagrams
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', lineHeight: 1.6 }}>
          Visual architecture diagrams for each core aspect of the AI Employee product.
          Select a diagram below to explore the system design.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              minHeight: 56,
              gap: 0.5,
            },
          }}
        >
          {DIAGRAMS.map((d, i) => (
            <Tab
              key={d.label}
              label={d.label}
              icon={d.icon}
              iconPosition="start"
              sx={{ flexDirection: 'row', gap: 0.8 }}
            />
          ))}
        </Tabs>
      </Paper>

      <Box>
        <ActiveDiagram />
      </Box>
    </Container>
  );
}
